import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './Content.css';
import PopupSettings from './PopupSettings';

const Content = () => {
  const [activeTab, setActiveTab] = useState('popup');
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    description: '',
    category: '',
    url_path: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const API_BASE = 'https://valleyapi-dr9y.onrender.com/api/admin/content';
  const getAdminToken = () => localStorage.getItem('admin_token');

  // Configuración del editor ReactQuill
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean']
    ]
  };

  // Fetch all content
  const fetchContents = async () => {
    setLoading(true);
    try {
      const token = getAdminToken();
      const response = await fetch(API_BASE, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setContents(data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Error loading content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, []);

  // ====================== DRAG & DROP HTML ======================
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (file.type !== 'text/html' && !file.name.endsWith('.html')) {
      toast.error('Solo se permiten archivos .html');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const htmlContent = event.target.result;
      setFormData(prev => ({ ...prev, description: htmlContent }));
      toast.success('¡Archivo HTML cargado correctamente!');
    };
    reader.readAsText(file);
  };

  // ====================== MODAL ======================
  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title || '',
        short_description: item.short_description || '',
        description: item.description || '',
        category: item.category || '',
        url_path: item.url_path || ''
      });
      setImagePreview(item.image_path ? `https://valleyapi-dr9y.onrender.com${item.image_path}` : null);
    } else {
      setEditingItem(null);
      setFormData({ title: '', short_description: '', description: '', category: '', url_path: '' });
      setImagePreview(null);
    }
    setSelectedImage(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ title: '', short_description: '', description: '', category: '', url_path: '' });
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Only images are allowed (JPG, PNG, GIF, WEBP)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must not exceed 5MB');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    if (!editingItem) setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSubmitting(true);
    try {
      const token = getAdminToken();
      const formDataToSend = new FormData();
      
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('short_description', formData.short_description.trim());
      formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('category', formData.category.trim() || 'Blog');
      formDataToSend.append('url_path', formData.url_path.trim());

      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      let url = API_BASE;
      let method = 'POST';

      if (editingItem) {
        url = `${API_BASE}/${editingItem.id}`;
        method = 'PUT';
      }

      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Error al guardar');

      toast.success(editingItem ? 'Content updated successfully' : 'Content created successfully');
      closeModal();
      fetchContents();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Error saving content');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${item.title}"?`)) return;

    try {
      const token = getAdminToken();
      const response = await fetch(`${API_BASE}/${item.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');
      toast.success('Content deleted successfully');
      fetchContents();
    } catch (error) {
      toast.error('Error deleting content');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const truncate = (text, max = 80) => {
    if (!text) return '';
    return text.length > max ? text.substring(0, max) + '...' : text;
  };

  return (
    <div className="admin-content-page">
      <div className="admin-content-header">
        <h1>
          <i className="fas fa-file-alt" style={{ marginRight: '12px', color: '#2b6cb0' }}></i>
          Content Management
        </h1>
        {activeTab === 'articles' && (
          <button className="btn-add" onClick={() => openModal()}>
            <i className="fas fa-plus"></i> Add New Content
          </button>
        )}
      </div>

      <div className="content-section-tabs" role="tablist" aria-label="Content sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'popup'}
          className={activeTab === 'popup' ? 'is-active' : ''}
          onClick={() => setActiveTab('popup')}
        >
          <i className="fas fa-window-restore" aria-hidden="true" /> Website pop-up
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'articles'}
          className={activeTab === 'articles' ? 'is-active' : ''}
          onClick={() => setActiveTab('articles')}
        >
          <i className="fas fa-newspaper" aria-hidden="true" /> Articles
        </button>
      </div>

      {activeTab === 'articles' ? (
        <>
      {/* Tabla de contenido */}
      {loading ? (
        <div className="content-loading"><div className="spinner"></div><p>Loading content...</p></div>
      ) : contents.length === 0 ? (
        <div className="content-empty">
          <i className="fas fa-folder-open"></i>
          <h3>No content yet</h3>
          <button className="btn-add" onClick={() => openModal()}>Add Content</button>
        </div>
      ) : (
        <div className="content-table-wrapper">
          <table className="content-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>ID</th>
                <th>Title</th>
                <th>Short Description</th>
                <th>Category</th>
                <th style={{ minWidth: '180px' }}>URL Path</th>
                <th>Date Added</th>
                <th style={{ width: '80px' }}>Image</th>
                <th style={{ width: '170px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contents.map(item => (
                <tr key={item.id}>
                  <td><span className="content-id">#{item.id}</span></td>
                  <td><div className="content-title">{item.title}</div></td>
                  <td><div className="content-short-desc">{truncate(item.short_description, 85)}</div></td>
                  <td><span className="content-category">{item.category || 'General'}</span></td>
                  <td>
                    {item.url_path ? (
                      <div className="content-url-path" style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '12px', 
                        color: '#2b6cb0',
                        wordBreak: 'break-all',
                        lineHeight: '1.3'
                      }}>
                        {item.url_path}
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontSize: '12px' }}>— not set —</span>
                    )}
                  </td>
                  <td><span className="content-date">{formatDate(item.date_added)}</span></td>
                  <td className="content-image-cell">
                    {item.image_path ? (
                      <img src={`https://valleyapi-dr9y.onrender.com${item.image_path}`} alt={item.title} className="content-image-thumb" onError={e => e.target.style.display = 'none'} />
                    ) : (
                      <div className="content-image-placeholder"><i className="fas fa-image"></i></div>
                    )}
                  </td>
                  <td>
                    <div className="content-actions">
                      <button className="btn-edit" onClick={() => openModal(item)} title="Edit"><i className="fas fa-edit"></i> Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(item)} title="Delete"><i className="fas fa-trash"></i> Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ==================== MODAL ==================== */}
      {showModal && (
        <div className="content-modal-overlay" onClick={closeModal}>
          <div className="content-modal" onClick={e => e.stopPropagation()}>
            <div className="content-modal-header">
              <h2>{editingItem ? 'Edit Content' : 'Add New Content'}</h2>
              <button className="content-modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="content-modal-body">
                {/* Title */}
                <div className="content-form-group">
                  <label>Title <span style={{color: '#e53e3e'}}>*</span></label>
                  <input type="text" name="title" value={formData.title} onChange={handleInputChange} required maxLength={255} />
                </div>

                {/* Short Description */}
                <div className="content-form-group">
                  <label>Short Description</label>
                  <textarea name="short_description" value={formData.short_description} onChange={handleInputChange} rows={3} />
                </div>

                {/* URL Path - NUEVO CAMPO */}
                <div className="content-form-group">
                  <label>URL Path (Custom URL)</label>
                  <input 
                    type="text" 
                    name="url_path" 
                    value={formData.url_path} 
                    onChange={handleInputChange} 
                    placeholder="/articles/machine-learning"
                    maxLength={255}
                    style={{ fontFamily: 'monospace' }}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '6px' }}>
                    Example: <strong>/articles/machine-learning</strong> → <strong>https://valleyintlsupply.com/articles/machine-learning</strong><br />
                    Leave the space empty to use the default URL (/articles/ID). It must be unique.
                  </small>
                </div>

                {/* === DRAG & DROP HTML + REACT QUILL === */}
                <div className="content-form-group">
                  <label>Full Article Content(HTML)</label>

                  {/* Drag & Drop Zone */}
                  <div
                    className={`html-dropzone ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      border: '2px dashed #2b6cb0',
                      borderRadius: '8px',
                      padding: '30px',
                      textAlign: 'center',
                      backgroundColor: isDragging ? '#f0f7ff' : '#fafafa',
                      marginBottom: '15px',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-file-code" style={{ fontSize: '42px', color: '#2b6cb0', marginBottom: '10px' }}></i>
                    <p style={{ margin: '8px 0 4px', fontWeight: 600 }}>
                      Move your file here <strong>.html</strong>
                    </p>
                    <small>Or click and select</small>
                    <input
                      type="file"
                      accept=".html"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => setFormData(prev => ({ ...prev, description: ev.target.result }));
                          reader.readAsText(file);
                        }
                      }}
                      style={{ display: 'none' }}
                      id="html-file-input"
                    />
                    <label htmlFor="html-file-input" style={{ cursor: 'pointer', color: '#2b6cb0', textDecoration: 'underline' }}>
                      Select .html file
                    </label>
                  </div>

                  {/* Editor ReactQuill */}
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    modules={quillModules}
                    style={{ height: '420px', marginBottom: '70px' }}
                    placeholder="El contenido del archivo HTML aparecerá aquí..."
                  />
                </div>

                {/* Category */}
                <div className="content-form-group">
                  <label>Category</label>
                  <select name="category" value={formData.category} onChange={handleInputChange}>
                    <option value="">Select a category</option>
                    <option value="Blog">Blog</option>
                    <option value="Services">Services</option>
                    <option value="Product News">Product News</option>
                  </select>
                </div>

                {/* Image Upload */}
                <div className="content-form-group">
                  <label>Image (Optional)</label>
                  <div className="content-image-upload">
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" onChange={handleImageChange} />
                    <div>
                      <i className="fas fa-cloud-upload-alt"></i>
                      <p>Click or drag an image here</p>
                      <small>JPG, PNG, GIF or WEBP • Max 5MB</small>
                    </div>
                  </div>

                  {imagePreview && (
                    <div className="content-image-preview">
                      <img src={imagePreview} alt="Preview" />
                      <div className="remove-image" onClick={removeSelectedImage}>
                        <i className="fas fa-times"></i> Remove
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="content-modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModal} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={submitting || !formData.title.trim()}>
                  {submitting ? 'Saving...' : (editingItem ? 'Update Content' : 'Create Content')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      ) : (
        <PopupSettings />
      )}
    </div>
  );
};

export default Content;
